import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { SolarRecommendationForm } from "../../model/solar-recommendation-form/solar-recommendation-form";
import { RootState } from "../store";
import { SolarProduct } from "../../model/solar-product/solar-product";
import { PaginatedList } from "../../model/common/paginated-list";
import { ApplianceItem } from "../../model/solar-recommendation-form/appliance-item";
import { 
  recommendProducts, 
  recommendBestFitProducts, 
  recommendBudgetFitProducts, 
  recommendPowerNeedsFitProducts 
} from "./solar-recommendation-thunk";

export enum RecommendationOptions {
    BEST_OFFER = 'Best offer',
    BUDGET_FIT = 'Budget fit',
    ENERGY_NEED = 'Energy need'
}

type SolarRecommendationSliceState = {
    error?: string;
    loading: boolean;
    option: RecommendationOptions;
    form: SolarRecommendationForm;
    productsThatFitTheBest?: SolarProduct[];
    productsThatFitTheBudget?: PaginatedList<SolarProduct>;
    productsThatFitThePowerNeeds?: PaginatedList<SolarProduct>;
}

type PageRequest = {
    page: number;
    pageSize: number;
}

function parseFormFromStorage(): SolarRecommendationForm {
    const formFromStorage = sessionStorage.getItem('solar-recommendation-form')

    const parsedForm: SolarRecommendationForm | undefined = formFromStorage ? JSON.parse(formFromStorage) : undefined

    return parsedForm ?? {
        appliances: [],
    };
}

function calculateTotalPower(appliances: ApplianceItem[]) {
    return appliances.reduce((acc, appliance) => acc + appliance.quantity * appliance.dailyUsage * appliance.appliance.power, 0);
}

function calculateTotalWatts(appliances: ApplianceItem[]) {
    return appliances.reduce((acc, appliance) => acc + appliance.quantity * appliance.appliance.power, 0);
}

function calculateAveragePower(appliances: ApplianceItem[]) {
    const averageDailyUsage = appliances.reduce((acc, appliance) => acc + appliance.dailyUsage, 0) / appliances.length;

    return appliances.reduce((acc, appliance) => acc + appliance.quantity * Math.min(averageDailyUsage, appliance.dailyUsage) * appliance.appliance.power, 0);
}

export const solarRecommendationSlice = createSlice({
    name: 'solarRecommendation',
    initialState: {
        form: parseFormFromStorage(),
        option: RecommendationOptions.BEST_OFFER,
        loading: false,
    } as SolarRecommendationSliceState,
    selectors: {
        form: (state) => state.form,
        productsThatFitTheBest: (state) => state.productsThatFitTheBest,
        productsThatFitTheBudget: (state) => state.productsThatFitTheBudget,
        productsThatFitThePowerNeeds: (state) => state.productsThatFitThePowerNeeds,
        error: (state) => state.error,
        totalPower: (state) => calculateTotalPower(state.form.appliances),
        totalWatts: (state) => calculateTotalWatts(state.form.appliances),
        averagePower: (state) => calculateAveragePower(state.form.appliances),
        countOfAppliances: (state) => state.form.appliances.length,
        bestOfferImage: (state) => state.productsThatFitTheBest?.[0]?.images?.[0]?.imageUrl,
        budgetOfferImage: (state) => state.productsThatFitTheBudget?.items?.[0]?.images?.[0]?.imageUrl,
        powerNeedsOfferImage: (state) => state.productsThatFitThePowerNeeds?.items?.[0]?.images?.[0]?.imageUrl,
        countOfBudgetOffers: (state) => state.productsThatFitTheBudget?.count ?? 0,
        countOfPowerNeedsOffers: (state) => state.productsThatFitThePowerNeeds?.count ?? 0,
        recommendedAlready: (state) => state.productsThatFitTheBest !== undefined || state.productsThatFitTheBudget !== undefined || state.productsThatFitThePowerNeeds !== undefined,
        selectedOption: (state) => state.option,
        productsToDisplay: (state) => {
            switch (state.option) {
                case RecommendationOptions.BEST_OFFER:
                    return state.productsThatFitTheBest;
                case RecommendationOptions.BUDGET_FIT:
                    return state.productsThatFitTheBudget?.items ?? [];
                case RecommendationOptions.ENERGY_NEED:
                    return state.productsThatFitThePowerNeeds?.items ?? [];
            }
        },
        currentPage: (state) => {
            switch (state.option) {
                case RecommendationOptions.BEST_OFFER:
                    return undefined;
                case RecommendationOptions.BUDGET_FIT:
                    return state.productsThatFitTheBudget?.pageIndex ?? 0;
                case RecommendationOptions.ENERGY_NEED:
                    return state.productsThatFitThePowerNeeds?.pageIndex ?? 0;
            }
        },
        numberOfPages: (state) => {
            switch (state.option) {
                case RecommendationOptions.BEST_OFFER:
                    return undefined;
                case RecommendationOptions.BUDGET_FIT:
                    return state.productsThatFitTheBudget?.totalPages ?? 0;
                case RecommendationOptions.ENERGY_NEED:
                    return state.productsThatFitThePowerNeeds?.totalPages ?? 0;
            }
        },
        loading: (state) => state.loading,
    },
    reducers: {
        init: (state) => {
            state.form = parseFormFromStorage();
            state.option = RecommendationOptions.BEST_OFFER;
            state.loading = false;
        },
        option: (state, action : PayloadAction<RecommendationOptions> ) => {
            state.option = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(recommendProducts.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(recommendProducts.fulfilled, (state, action) => {
            state.productsThatFitTheBest = action.payload.bestFit;
            state.productsThatFitTheBudget = action.payload.budgetFit;
            state.productsThatFitThePowerNeeds = action.payload.powerNeedsFit;
            state.loading = false;
        });
        builder.addCase(recommendProducts.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });
        builder.addCase(recommendBestFitProducts.pending, (state) => {
            state.error = undefined;
            state.loading = true;
        });
        builder.addCase(recommendBestFitProducts.fulfilled, (state, action) => {
            state.productsThatFitTheBest = action.payload;
            state.loading = false;
        });
        builder.addCase(recommendBestFitProducts.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });
        builder.addCase(recommendBudgetFitProducts.pending, (state) => {
            state.error = undefined;
            state.loading = true;
        });
        builder.addCase(recommendBudgetFitProducts.fulfilled, (state, action) => {
            state.productsThatFitTheBudget = action.payload;
            state.loading = false;
        });
        builder.addCase(recommendBudgetFitProducts.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });
        builder.addCase(recommendPowerNeedsFitProducts.pending, (state) => {
            state.error = undefined;
            state.loading = true;
        });
        builder.addCase(recommendPowerNeedsFitProducts.fulfilled, (state, action) => {
            state.productsThatFitThePowerNeeds = action.payload;
            state.loading = false;
        });
        builder.addCase(recommendPowerNeedsFitProducts.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });
    }
});

export const { 
    init,
    option
} = solarRecommendationSlice.actions;

export { 
    recommendProducts, 
    recommendBestFitProducts, 
    recommendBudgetFitProducts, 
    recommendPowerNeedsFitProducts 
}

export const { 
    form, 
    productsThatFitTheBest, 
    productsThatFitTheBudget, 
    productsThatFitThePowerNeeds, 
    error, 
    averagePower, 
    totalPower, 
    totalWatts, 
    countOfAppliances,  
    bestOfferImage,
    budgetOfferImage,
    powerNeedsOfferImage,
    countOfBudgetOffers,
    countOfPowerNeedsOffers,
    recommendedAlready,
    selectedOption,
    productsToDisplay,
    currentPage,
    numberOfPages,
    loading,
} = solarRecommendationSlice.selectors;

export default solarRecommendationSlice.reducer;